import { z } from 'zod';
import { resolvePlace } from '@/utils/places';

/**
 * Validation for the ride search form.
 *
 * <p>Zod schema first, types derived from it. Writing the TypeScript interface by hand
 * and validating separately lets the two disagree, and the disagreement surfaces at
 * runtime in production rather than at compile time here.
 *
 * <p>This mirrors, but does not replace, the backend's validation. The server cannot
 * trust anything a browser sends; this exists to give the user an answer immediately
 * instead of after a round trip.
 */

/** Today's date as `yyyy-MM-dd` in the *browser's* timezone. */
export function todayIsoDate(now: Date = new Date()): string {
  // Deliberately not `toISOString().slice(0, 10)`, which converts to UTC first and so
  // reports "yesterday" for anyone east of Greenwich during their evening -- for users
  // in India, every evening.
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const MAX_PASSENGERS = 8;

export const rideSearchSchema = z
  .object({
    from: z
      .string()
      .trim()
      .min(2, 'Enter where you are leaving from')
      .max(120, 'That place name is too long')
      .refine((v) => resolvePlace(v) !== undefined, 'Choose a city from the suggestions'),
    to: z
      .string()
      .trim()
      .min(2, 'Enter where you are going')
      .max(120, 'That place name is too long')
      .refine((v) => resolvePlace(v) !== undefined, 'Choose a city from the suggestions'),
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a travel date')
      .refine((value) => value >= todayIsoDate(), 'Choose today or a later date'),
    passengers: z.coerce
      .number()
      .int('Whole seats only')
      .min(1, 'At least one seat')
      .max(MAX_PASSENGERS, `Up to ${MAX_PASSENGERS} seats per booking`),
  })
  .refine((value) => value.from.trim().toLowerCase() !== value.to.trim().toLowerCase(), {
    message: 'Origin and destination must be different',
    path: ['to'],
  });

export type RideSearchInput = z.input<typeof rideSearchSchema>;
export type RideSearchValues = z.output<typeof rideSearchSchema>;

/** Serialises search values into URL query parameters, so a search is shareable. */
export function toSearchParams(values: RideSearchValues): URLSearchParams {
  return new URLSearchParams({
    from: values.from,
    to: values.to,
    date: values.date,
    passengers: String(values.passengers),
  });
}
